import { CashBalanceResponse } from './cashBalance';
import { CurrencyCode } from './currencyCode';
import { Payment } from './payments';

export interface Policy2 {
	// Required
	characteristics: PolicyCharacteristics[];
	createdTimestamp: string;
	configVersion?: string;
	displayId: string;
	documents: PolicyDocument[];
	effectiveContractEndTimestamp: string;
	exposures: Exposure[];
	fees: Fee[];
	grossFees: string;
	grossFeesCurrency: string;
	invoices: PolicyInvoice2Response[];
	locator: string;
	modifications: PolicyModification[];
	originalContractEndTimestamp: string;
	originalContractStartTimestamp: string;
	policyholderLocator: string;
	premiumReportName: string;
	productLocator: string;
	productName: string;
	updatedTimestamp: string;
	state: 'Application' | 'Quoted' | 'Invoiced' | 'Issued';
	currency: CurrencyCode;
	cashBalance: CashBalanceResponse;

	// Optional
	cancellation?: CancellationInfo;
	issuedTimestamp?: string;
	paymentScheduleName?: string;
	quotes?: QuoteResponse[];

	// Added for consumption by frontend, not in api
	tenantLocator: string;
	updatedBy: string;
	createdBy: string;
	resourcesAsOfTimestamp?: string;
	overrideResourcesAsOfTimestamp?: string;
}

export interface CancellationInfo {
	effectiveTimestamp: string;
	modificationLocator: string;
	modificationName: string;
}

export interface PolicyModification {
	locator: string;
	name: string;
	configVersion: string;
	displayId: string;
	number: string;
	issuedTimestamp?: string;
	fieldValues: FieldValuesMap;
	fieldGroupsByLocator: FieldGroupsByLocatorMap;
	newPolicyCharacteristicsLocator?: string;
	newPolicyCharacteristicsLocators: string[];
	exposureModifications: ExposureModification[];
	automatedUnderwritingResult?: AutomatedUnderwritingResult;
	tenantLocator: string;
	policyholderLocator: string;
	productLocator: string;
	policyLocator: string;
	policyEndTimestamp: string;
	createdTimestamp: string;
	updatedTimestamp: string;
	effectiveTimestamp: string;
	createdBy: string;
	updatedBy: string;
	cancellationName?: string;
	endorsementLocator?: string;
	renewalLocator?: string;
	resourcesAsOfTimestamp?: string;
}

export interface ExposureModification {
	locator: string;
	newExposureCharacteristicsLocator?: string;
	perilModifications: PerilModification[];
	tenantLocator: string;
	policyholderLocator: string;
	productLocator: string;
	policyLocator: string;
}

export interface PerilModification {
	locator: string;
	perilLocator: string;
	exposureModificationLocator: string;
	newPerilCharacteristicsLocator?: string;
	replacedPerilCharacteristicsLocator?: string;
	tenantLocator: string;
	policyholderLocator: string;
	productLocator: string;
	policyLocator: string;
}

export interface CancellationCreateRequest {
	name: string;
	cancellationCategory: string;
	effectiveTimestamp: string;
	conflictHandling?: 'block' | 'invalidate'; // optional, defaults to 'block'
	issue?: boolean; // optional, defaults to false
}

export type FieldGroupsByLocatorMap = { [locator: string]: FieldValuesMap };

export interface PolicyCharacteristics {
	locator: string;
	policyLocator: string;
	policyStartTimestamp: string;
	policyEndTimestamp: string;
	startTimestamp: string;
	endTimestamp: string;
	grossPremium?: string;
	grossPremiumCurrency: string;
	grossTaxes?: string;
	grossTaxesCurrency: string;
	issuedTimestamp?: string;
	fieldValues: FieldValuesMap;
	fieldGroupsByLocator: FieldGroupsByLocatorMap;
	mediaByLocator?: any;
	tenantLocator: string;
	policyholderLocator: string;
	productLocator: string;
	createdTimestamp: string;
	createdBy: string;
	replacedTimestamp?: string;
}

export interface QuoteResponse {
	// Required
	createdTimestamp: string;
	locator: string;
	name: string;
	policyLocator: string;
	policyholderLocator: string;
	productLocator: string;
	productName: string;
	contractStartTimestamp: string;
	contractEndTimestamp: string;
	policyModificationName: string;
	state:
		| 'active'
		| 'withdrawn'
		| 'rejected_by_underwriter'
		| 'expired'
		| 'accepted_by_policyholder'
		| 'no_underwriting_decision'
		| 'rejected_by_policyholder';
	fees: FeeChange[]; // Fee Response
	grossFees: string;
	grossFeesCurrency: string;
	paymentScheduleName: string;
	characteristics: PolicyCharacteristics;
	exposures: Exposure[];
	documents: Document[];
}

export interface PolicyQuoteUpdateRequest {
	addExposures?: ExposureCreateRequest[];
	addFieldGroups?: FieldGroupCreateRequest[];
	fieldValues?: FieldValuesMap;
	name?: string;
	paymentScheduleName?: string;
	policyEndTimestamp?: string;
	policyStartTimestamp?: string;
	removeExposures?: string[];
	removeFieldGroups?: string[];
	updateExposures?: ExposureUpdateRequest[];
	updateFieldGroups?: FieldGroupUpdateRequest[];
	autofill?: string[] | null;
	overrideResourcesAsOfTimestamp?: string;
	resetOverrideResourcesAsOfTimestamp?: boolean;
}

export interface FinancialTransaction {
	startTimestamp: string;
	endTimestamp: string;
	postedTimestamp: string;
	amount: string;
	amountCurrency?: string;
	policyModificationLocator: string;
	type: string;
	perilName?: string;
	perilCharacteristicsLocator?: string;
	taxName?: string;
	taxLocator?: string;
	feeName?: string;
	feeLocator?: string;
}

export interface PolicyInvoice2StatusAndTime {
	status: PolicyInvoice2Status;
	timestamp: string;
}

export type PolicyInvoice2SettlementStatus = 'outstanding' | 'settled';
export type PolicyInvoice2SettlementType =
	| 'paid'
	| 'writtenOff'
	| 'zeroDue'
	| 'invalidated';
export type PolicyInvoice2Status = 'unfulfilled' | PolicyInvoice2SettlementType;
export interface PolicyInvoice2Response {
	locator: string;
	createdTimestamp: string;
	policyLocator: string;
	displayId: string;
	statuses: PolicyInvoice2StatusAndTime[];
	settlementStatus: PolicyInvoice2SettlementStatus;
	settlementType?: PolicyInvoice2SettlementType;
	payments?: Payment[];
	documents: PolicyDocument[];
	dueTimestamp: string;
	totalDue: string;
	totalDueCurrency: CurrencyCode;
	startTimestamp: string;
	endTimestamp: string;
	paid?: boolean;
	transactionIssued?: boolean;
	invoiceType?: string;
}

export interface FutureInvoiceResponse {
	issueTimestamp: string;
	startTimestamp: string;
	endTimestamp: string;
	financialTransactions: FinancialTransaction[];
	dueTimestamp?: string;
	writeOff?: boolean;
}

export interface GeneratedInvoiceResponse {
	financialTransactions: FinancialTransaction[];
	invoice: PolicyInvoice2Response;
}

export interface CheckInvoicesResponse {
	futureInvoices: FutureInvoiceResponse[];
	generatedInvoices: GeneratedInvoiceResponse[];
}

export interface PaymentCreateRequest {
	amount?: number;
	fieldValues?: FieldValuesMap;
}

export interface InvoicePaymentRequest {
	// optional (exactly one and only one of these in a payment creation request)
	paymentLocator?: string;
	payment?: PaymentCreateRequest;
	fieldValues?: FieldValuesMap; // backwards compatibility with current API
}

export interface PaymentReversalReason {
	name: string;
	details: string;
}

// https://docs.socotra.com/production/api/policy.html#FinancialTransactionResponse
interface FinancialTransactionResponse {
	startTimestamp: number;
	endTimestamp: number;
	postedTimestamp: number;
	amount: number;
	policyModificationLocator: string;
	type: 'premium' | 'tax' | 'fee' | 'commission' | 'holdback' | 'cash';
	perilName?: string;
	perilCharacteristicsLocator?: string;
	amountCurrency?: string;
	commissionLocator?: string;
	commissionRecipient?: string;
	feeLocator?: string;
	feeName?: string;
	holdbackLocator?: string;
	taxLocator?: string;
	taxName?: string;
}

// https://docs.socotra.com/production/api/quotes.html#PolicyQuoteInvoiceResponse
export interface PolicyQuoteInvoiceResponse {
	issueTimestamp: number;
	startTimestamp: number;
	endTimestamp: number;
	financialTransactions: FinancialTransactionResponse[];
	dueTimestamp?: number;
}

export interface PolicyQuoteResponse {
	characteristics: PolicyCharacteristics;
	contractEndTimestamp: string;
	contractStartTimestamp: string;
	createdTimestamp: string;
	documents: PolicyDocument[];
	exposures: Exposure[];
	fees: FeeChange[];
	grossFees?: number;
	grossFeesCurrency?: string;
	invoices: PolicyQuoteInvoiceResponse[];
	locator: string;
	name: string;
	state: string;
	paymentScheduleName: string;
	policyholderLocator: string;
	policyLocator: string;
	selected: boolean;
	updatedTimestamp: string;
	// Optional
	automatedUnderwritingResult: AutomatedUnderwritingResult;
	resourcesAsOfTimestamp?: string;
	overrideResourcesAsOfTimestamp?: string;
}

export type AutomatedUnderwritingDecision = 'accept' | 'reject' | 'none';

export interface AutomatedUnderwritingResult {
	decision: AutomatedUnderwritingDecision;
	notes: string[];
	decisionTimestamp: string;
}

export interface GraceLapseReinstatementResponse {
	gracePeriod: GracePeriodResponse;
	reinstatements: ReinstatementResponse[];
	lapse?: LapseResponse;
}

export interface GracePeriodResponse {
	endTimestamp: string;
	invoiceLocator: string;
	locator: string;
	policyholderLocator: string;
	policyId: string;
	policyLocator: string;
	productLocator: string;
	startTimestamp: string;
	cancellationLocator?: string;
	document?: PolicyDocument;
	settledTimestamp?: string;
	cancelEffectiveTimestamp?: string;
}

export interface GraceDocument {
	locator: string;
	url: string;
	urlExpirationTimestamp: string;
	fileName: string;
	displayName: string;
	type: string;
	createdTimestamp: string;
}

export interface ReinstatementResponse {
	createdTimestamp: string;
	currentStatus: string; // open | closed | invoiced | issued
	locator: string;
	statuses: ReinstatementStatusAndTimeResponse[];
	updatedTimestamp: string;
	uploadedDocuments: PolicyDocument[];
	invoicedTimestamp?: string;
	invoiceLocator?: string;
	issuedTimestamp?: string;
	reinstatementDocument?: PolicyDocument;
	reinstatementTimestamp: string;
}

export interface ReinstatementStatusAndTimeResponse {
	status: string; // open | closed | invoiced | issued
	timestamp: string;
}

export interface LapseResponse {
	createdTimestamp: string;
	lapseTimestamp: string;
	locator: string;
	reinstatementPeriodEndTimestamp: string;
	document?: PolicyDocument;
}

export interface PolicyDocument {
	locator: string;
	url: string;
	urlExpirationTimestamp: string;
	fileName: string;
	displayName: string;
	type: 'pdf' | 'html';
}

export interface FeeChange {
	name: string;
	amount?: string;
	amountCurrency?: string;
	newAmount?: string;
	amountChange?: string;
}

export interface Fee {
	name: string;
	amount?: string;
	amountCurrency?: string;
	description?: string;
	startTimestamp?: string;
	endTimestamp?: string;
	reversed?: boolean;
	locator?: string;
}

export interface Exposure {
	locator: string;
	name: string;
	displayId: string;
	characteristics: ExposureCharacteristics[];
	perils: Peril[];
	tenantLocator?: string;
	policyholderLocator: string;
	productLocator: string;
	policyLocator: string;
	createdTimestamp: string;
	updatedTimestamp: string;
	createdBy?: string;
	updatedBy?: string;
}

export type FieldValues = string[];
export type FieldValuesMap = { [name: string]: FieldValues };

export interface ExposureCharacteristics {
	locator: string;
	startTimestamp: string;
	endTimestamp: string;
	exposureLocator: string;
	issuedTimestamp?: string;
	fieldValues: FieldValuesMap;
	fieldGroupsByLocator: FieldGroupsByLocatorMap;
	tenantLocator: string;
	policyholderLocator: string;
	productLocator: string;
	policyLocator: string;
	createdTimestamp: string;
	createdBy: string;
	replacedTimestamp?: string;
	mediaByLocator?: any;
}

export interface Peril {
	locator: string;
	displayId: string;
	name: string;
	exposureLocator: string;
	renewalGroup: string;
	createdTimestamp: string;
	characteristics: PerilCharacteristics[];
}

export interface PerilCharacteristics {
	locator: string;
	perilLocator: string;
	issuedTimestamp?: string;
	exposureCharacteristicsLocator: string;
	policyCharacteristicsLocator: string;
	indemnityPerItem?: string;
	indemnityPerEvent?: string;
	indemnityInAggregate?: string;
	lumpSumPayment?: string;
	deductible?: string;
	premium?: string;
	premiumCurrency: string;
	coverageStartTimestamp: string;
	coverageEndTimestamp: string;
	originalCoverageEndTimestamp: string;
	addFieldGroups: FieldGroupCreateRequest[];
	removeFieldGroups: string[];
	updateFieldGroups: FieldGroupUpdateRequest;
	fieldValues: FieldValuesMap;
	fieldGroupsByLocator: FieldGroupsByLocatorMap;
	endChangeBound?: string;
	policyModificationLocator: string;
	tenantLocator: string;
	policyholderLocator: string;
	productLocator: string;
	policyLocator: string;
	createdTimestamp: string;
	updatedTimestamp: string;
	createdBy: string;
	updatedBy: string;
	replacedTimestamp?: string;
	mediaByLocator?: any;
}

export interface FieldGroupUpdateRequest {
	fieldGroupLocator: string;
	fieldName: string;
	fieldValues: FieldValuesMap;
}

export interface ExposureCreateRequest {
	exposureLocator?: string;
	locator?: string;
	exposureName: string;
	fieldValues?: FieldValuesMap;
	fieldGroups?: FieldGroupCreateRequest[];
	perils?: PerilCreateRequest[];
}

export interface ExposureCreateRequestHelpers {
	/**
	 * A remnant of FastForms. New fieldGroups get stored here everywhere else,
	 * so it's best to keep it and change it on the actual request than to update the form and accommodate this one off.
	 * @type {FieldGroupCreateRequest[]}
	 * @memberof ExposureCreateRequest
	 */
	addFieldGroups?: FieldGroupCreateRequest[];
	allAddFieldGroupsDeleted?: { [key: string]: boolean };
}

export interface ExposureUpdateRequest extends FieldGroupsUpdateRequest {
	exposureLocator: string;
	fieldValues?: FieldValuesMap;
	addPerils?: PerilCreateRequest[];
	updatePerils?: PerilUpdateRequest[];
	removePerils?: string[];
}

export interface PerilCreateRequest {
	name: string;
	temporaryLocator?: string; // ignored by backend - used for tracking create requests on frontend only
	fieldValues?: FieldValuesMap;
	fieldGroups?: FieldGroupCreateRequest[];
	indemnityPerItem?: string;
	indemnityPerEvent?: string;
	indemnityInAggregate?: string;
	lumpSumPayment?: string;
	deductible?: string;
	locator?: string; // BE returns a unique locator for peril create requests
}

export interface PerilCreateRequestHelpers {
	removeDeductible?: boolean;
	removeIndemnityInAggregate?: boolean;
	removeLumpSumPayment?: boolean;
	removeIndemnityPerEvent?: boolean;
	removeIndemnityPerItem?: boolean;
	addFieldGroups: FieldGroupCreateRequest[];
	allAddFieldGroupsDeleted: { [key: string]: boolean };
}

export interface FieldGroupCreateRequest {
	locator?: string;
	fieldName: string;
	fieldValues: FieldValuesMap;
}

export interface PerilUpdateRequest
	extends FieldGroupsUpdateRequest,
		PerilUpdateRequestHelpers {
	perilLocator: string;
	indemnityPerItem?: string;
	removeIndemnityPerItem?: boolean;
	indemnityPerEvent?: string;
	removeIndemnityPerEvent?: boolean;
	indemnityInAggregate?: string;
	removeIndemnityInAggregate?: boolean;
	lumpSumPayment?: string;
	removeLumpSumPayment?: boolean;
	deductible?: string;
	removeDeductible?: boolean;
	fieldValues?: FieldValuesMap;
	locator?: string;
}

export interface PerilUpdateRequestHelpers {
	allAddFieldGroupsDeleted?: { [key: string]: boolean };
	addFieldGroups?: FieldGroupCreateRequest[];
}

export interface FieldGroupsUpdateRequest {
	addFieldGroups?: FieldGroupCreateRequest[];
	updateFieldGroups?: FieldGroupUpdateRequest[];
	removeFieldGroups?: string[];
}

export interface PolicyPriceResponse {
	// Required
	commissions: Commission[];
	exposurePrices: GetExposurePriceResponse[];
	fees: Fee[];
	grossCommissions: string;
	grossCommissionsCurrency: string;
	grossFees: string;
	grossFeesCurrency: string;
	grossPremium: string;
	grossPremiumCurrency: string;
	grossTaxes: string;
	grossTaxesCurrency: string;
	grossTechnicalPremiumCurrency: string;
	premiumsByExposureLocator: { [locator: string]: string }; // no longer used in frontend
	premiumsByPerilLocator: { [locator: string]: string }; // no longer used in frontend
	taxGroups: TaxGroup[];
	totalDue: string;
	totalDueCurrency: string;
	// Optional
	grossTechnicalPremium?: string;
	// Added for frontend consumption, not in api
	total?: string;
	totalCurrency?: string;
}

export interface GetExposurePriceResponse {
	commissions: Commission[];
	grossCommissions: string;
	grossCommissionsCurrency: string;
	grossTechnicalPremium?: string;
	grossTechnicalPremiumCurrency: string;
	grossPremium: string;
	grossPremiumCurrency: string;
	grossTaxes: string;
	grossTaxesCurrency: string;
	locator: string;
	name: string;
	perilPrices: GetPerilPriceResponse[];
	taxGroups: TaxGroup[];
	total: string;
	totalCurrency: string;
}

export interface Commission {
	amount: string;
	amountCurrency: string;
	recipient: string;
}

export interface GetPerilPriceResponse {
	commissions: Commission[];
	grossCommissions: string;
	grossCommissionsCurrency: string;
	grossTaxes: string;
	grossTaxesCurrency: string;
	locator: string;
	name: string;
	premium: string;
	premiumCurrency: string;
	technicalPremium?: string;
	technicalPremiumCurrency: string;
	taxes: TaxGroup[];
	total: string;
	totalCurrency: string;
}

export interface TaxGroup {
	name: string;
	amount: string;
	amountCurrency: string;
}

export interface PolicyPriceChangeResponse {
	fees: FeeChange[];
	commissions: CommissionChangeResponse[];
	exposurePrices: ExposurePriceChangeResponse[];
	grossCommissionsChange: string;
	grossPremiumChange: string;
	grossFeesChange: string;
	grossTaxesChange: string;
	holdbacks: HoldbackResponse[];
	newGrossCommissions: string;
	newGrossPremium: string;
	newGrossFees: string;
	newGrossTaxes: string;
	newTotal: string;
	taxGroups: TaxChangeResponse[];
	totalChange: string;
}

export interface HoldbackResponse {
	amount: string;
	cancellationLocator: string;
	isReversed: boolean;
	locator: string;
	type: string; // premium | tax | fee | commission | holdback
	commissionRecipient?: string;
	feeLocator?: string;
	feeName?: string;
	holdbackMetadata?: string;
	perilCharacteristicsLocator?: string;
	perilName?: string;
	taxLocator?: string;
	taxName?: string;
}

export interface CommissionChangeResponse {
	amountChange: string;
	newAmount: string;
	recipient: string;
}

export interface ExposurePriceChangeResponse {
	commissions: CommissionChangeResponse[];
	grossPremiumChange: string;
	grossTaxesChange: string;
	name: string;
	newGrossPremium: string;
	newGrossTaxes: string;
	perilPrices: PerilPriceChangeResponse[];
	taxGroups: TaxChangeResponse[];
	locator?: string;
	newlyAdded?: boolean;
}

export interface PerilPriceChangeResponse {
	commissions: CommissionChangeResponse[];
	grossPremiumChange: string;
	grossTaxesChange: string;
	displayName?: string;
	name: string;
	newGrossPremium: string;
	newGrossTaxes: string;
	taxes: TaxChangeResponse[];
	locator?: string;
}

export interface TaxChangeResponse {
	change: string;
	name: string;
	newAmount: string;
}

export enum PerilCoverageType {
	COVERAGE_SPANS_EXPOSURE = 'COVERAGE_SPANS_EXPOSURE',
	LIMITED_COVERAGE_OVER_TIME = 'LIMITED_COVERAGE_OVER_TIME',
	COVERAGE_NEVER_IN_FORCE = 'COVERAGE_NEVER_IN_FORCE',
}

export interface HasCoverage {
	coverageStartTimestamp?: string;
	coverageEndTimestamp?: string;
	replacedTimestamp?: string;
}

export interface PerilCoverage {
	characteristics?: PerilCharacteristics;
	coverageStartTimestamp?: string;
	coverageEndTimestamp?: string;
	coverageType: PerilCoverageType;
}

export interface PolicyUpdateRequest extends FieldGroupsUpdateRequest {
	fieldValues?: FieldValuesMap;
	policyStartTimestamp?: string;
	policyEndTimestamp?: string;
	overrideResourcesAsOfTimestamp?: string;
	resetOverrideResourcesAsOfTimestamp?: boolean;
	addExposures?: ExposureCreateRequest[];
	updateExposures?: ExposureUpdateRequest[];
	removeExposures?: string[];
	paymentScheduleName?: string;
	addFieldGroups?: FieldGroupCreateRequest[];
	updateFieldGroups?: FieldGroupUpdateRequest[];
	removeFieldGroups?: string[];
	autofill?: string[] | null;
}

export interface PaymentScheduleChangeRequest {
	newPaymentScheduleName: string;
}

export interface FeeCreateRequest {
	feeName: string;
	description: string;
	amount: number;
	startTimestamp: string;
	endTimestamp: string;
}

export interface FeeUpdateRequest {
	newFees?: [FeeCreateRequest];
	reverseFees?: [string];
}

export interface FeeCreateResponse {
	feeName: string;
	description: string;
	amount: number;
	startTimestamp: string;
	endTimestamp: string;
}

export interface PolicyFeeSummaryResponse {
	fees: Fee[];
}
